import { useEffect, useState } from 'react'
import { Alert, AlertColor, AlertTitle, Box, Button, Grid2, Snackbar, Typography } from '@mui/material'
import { isEmpty, isNil } from 'lodash/fp'
import { hasExeededUsageLimitApi, isAlreadyRequestedApi, sendRequestData } from './function'
import log from 'loglevel'
import { FormInput } from '../../functions/types'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormInputSchema } from '../../functions/schema'
import InputForm from '../InputForm'
import alertPropaties from './alert-propaties.json'
import { useAuth0 } from '@auth0/auth0-react'
import { isAfter, parseISO } from 'date-fns/fp'
import ClosedPage from '../ClosedPage'
import { ClosedReason } from '../../types'
import { Loading } from '../Loading'
import breadCrumbs from './img/2026aw_select_cp_breadcrumbs.jpg'
import { useWindowSize } from '../../functions/common'

const env = process.env['REACT_APP_ENV'] ?? 'dev'

log.setLevel(log.levels.DEBUG)

export const FormProgress = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { width } = useWindowSize()
  const [alertStatus, setAlertStatus] = useState<AlertColor | undefined>(undefined)
  const [isSending, setIsSending] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasExeededCouponLimit, setHasExeededCouponLimit] = useState<boolean | undefined>(false)
  const [isAlreadyRequested, setIsAlreadyRequested] = useState<boolean | undefined>(false)
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false)
  const campaignCode = { prd: '2b62310a-bf7f-484b-880e-3c70b4f975d2', stg: 'c9c9f5ea-0b69-4d3d-920d-578f59736883-dummy', dev: 'c9c9f5ea-0b69-4d3d-920d-578f59736883-dummy' }[env] as string
  const closedDate = parseISO('2026-02-01 00:00:00')
  const today = new Date()
  const isAfterClosedDate = isAfter(closedDate)(today)

  useEffect(() => {
    const check = async () => {
      const token = await getAccessTokenSilently()
      hasExeededUsageLimitApi(token, campaignCode).then((res) => {
        if (isNil(res)) {
          setIsMaintenance(true)
        } else {
          setHasExeededCouponLimit(!res)
          isAlreadyRequestedApi(token, campaignCode).then((res) => {
            setIsAlreadyRequested(res)
            if (isNil(hasExeededCouponLimit) || isNil(isAlreadyRequested)) {
              setIsMaintenance(true)
            }
            setIsLoading(false)
          }).catch(() => {
            setIsMaintenance(true)
          })
        }
      }).catch(() => {
        setIsMaintenance(true)
      })
    }
    check()
  }, [campaignCode])

  const redirectToUrl = (url: string) => {
    window.location.href = url
  }

  const methods = useForm<FormInput>({
    resolver: zodResolver(FormInputSchema),
    defaultValues: {
      No1: undefined
    },
    mode: 'onSubmit'
  })
  const { handleSubmit } = methods

  const onSubmit: SubmitHandler<FormInput> = (_data) => {
    setIsSending(true)
    getAccessTokenSilently({ authorizationParams: { audience: "https://app.fdh.miraiz-connect.jp/" } }).then((token: string) => {
      sendRequestData(token, campaignCode).then((result) => {
        if (result) {
          redirectToUrl(`https://www.miraiz-connect-tsunagu.com/pages/2025awkatenecp_entry${{prd: "", stg: "", dev: "?development_mode=true"}[env] as string}`)
        } else {
          setIsSending(false)
          setAlertStatus("error")
        }
      }).catch((e) => {
        setIsSending(false)
        throw e
      })
    }).catch((error) => {
      setIsSending(false)
    })
  }

  const handleAlertClose = () => {
    setAlertStatus(undefined)
  }

  return (
    <Box>
      {isLoading ? <Loading /> :
        <Grid2 container>
          <Grid2 size={{ sm: 2 }} />
          <Grid2 size={{ lg: 8, sm: 8 }} spacing={10}>
            {
              isMaintenance ?
                <ClosedPage reason={ClosedReason.campaignMaintenance} /> :
                isAfterClosedDate ?
                  <ClosedPage reason={ClosedReason.expired} /> :
                  hasExeededCouponLimit ?
                    <ClosedPage reason={ClosedReason.couponUseageLimit} /> :
                    isAlreadyRequested ?
                      <ClosedPage reason={ClosedReason.alreadyRequested} /> :
                      <Box>
                        <Box display={"flex"} justifyContent={"center"}>
                          <Box
                            component="img"
                            sx={{ height: width >= 470 ? 140 : 100 }}
                            alt="breadCrumbs"
                            src={breadCrumbs}
                            position={"relative"}
                          />
                        </Box>
                        <Typography ><FormProvider {...methods}><InputForm /></FormProvider></Typography>
                        <Box display={"flex"} justifyContent={"center"} paddingTop={3}>
                          <Box display={"grid"} justifyContent={"normal"}>
                            <Button sx={{ height: 50, fontSize: 30, backgroundColor: "#4BB192", borderRadius: "25px" }} variant="contained" color="success" onClick={handleSubmit(onSubmit)} disabled={isSending}>
                              {"次に進む"}
                            </Button>
                            <Typography color="gray" sx={{ whiteSpace: 'pre-line' }}>{`※TSUNAGU STOREのサイトに遷移します。`}</Typography>
                          </Box>
                        </Box>
                        <Box display={"flex"} justifyContent={"center"} marginTop={9}>
                          <Typography color="gray" sx={{ whiteSpace: 'pre-line', fontSize: 12 }}>{`
                            ※ブラウザ設定がシークレットモード（プライベートモード）になっていると、お手続きが正常に進まない可能性がございます。
                            お使いの端末のセキュリティ設定をご確認の上、お手続きください。
                            `}</Typography>
                        </Box>
                      </Box>
            }
          </Grid2>
          <Snackbar open={!isEmpty(alertStatus)} autoHideDuration={5000} onClose={handleAlertClose}>
            <Alert severity={alertStatus}>
              <AlertTitle>{!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertTitle"] : ""}</AlertTitle>
              {!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertMessage"] : ""}
            </Alert>
          </Snackbar>

        </Grid2>}
    </Box>
  )
}

export default FormProgress